import type { StringifyObjectId } from '$lib/interfaces/general/basicModel';
import type { ClassValue } from 'clsx';
import clsx from 'clsx';
import _ from 'lodash';
import mongoose from 'mongoose';
import { twMerge } from 'tailwind-merge';
import type { ZodError } from 'zod';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export const startCase = (text: string) => {
	return text.replace(/\w+/g, _.capitalize);
};

export const truncateObjectId = (_id: string) => {
	try {
		return _id.substring(0, 4) + '...' + _id.substring(20);
	} catch (error) {
		return _id;
	}
};

export const getValidationMessages = (error: ZodError) => {
	return error.issues.map((issue) => `${issue.path.length > 0 ? issue.path.join('.') : 'form'}: ${issue.message.toLowerCase()}`).join('\n');
};

export const sleep = async (ms: number) => {
	return await new Promise((resolve) => {
		setTimeout(() => {
			resolve(true);
		}, ms);
	});
};

export const validationError = (error: ZodError, message = ''): App.Error => {
	return {
		message: message || getValidationMessages(error),
		fieldErrors: error.flatten().fieldErrors,
		formErrors: error.flatten().formErrors
	};
};

/**
 * @deprecated as it will transform Date type to string that may cause a problem. use `serializeDocument` instead!
 */
export const serializeModel = <T>(data: T): StringifyObjectId<T> => JSON.parse(JSON.stringify(data));

/**
 * This parse all ObjectId to string recursively
 */
export const serializeDocument = <T>(input: T): StringifyObjectId<T> => {
	if (input === null || typeof input !== 'object') {
		return input as StringifyObjectId<T>;
	}

	if (Array.isArray(input)) {
		return input.map((item) => serializeDocument(item)) as StringifyObjectId<T>;
	}

	return Object.keys(input).reduce((acc, key) => {
		const value = input[key as keyof T];

		if (value instanceof mongoose.Types.ObjectId) {
			acc[key as keyof T] = value.toHexString() as never;
		} else if (value instanceof Date) {
			acc[key as keyof T] = value as never;
		} else {
			acc[key as keyof T] = serializeDocument(value) as never;
		}

		return acc;
	}, {} as StringifyObjectId<T>);
};

export const chunkArray = (array: number[], chunkSize: number) => {
	const result = [];
	for (let i = 0; i < array.length; i += chunkSize) {
		result.push(array.slice(i, i + chunkSize));
	}
	return result;
};

export const findNumberInNestedArray = (nestedArray: number[][], targetNumber: number) => {
	for (const [i, innerArray] of nestedArray.entries()) {
		const j = innerArray.indexOf(targetNumber);
		if (j !== -1) {
			return i;
		}
	}
	return 0;
};
